import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchAPI } from '@/api/client'
import { POLL_INTERVAL_MS } from '@/constants/thresholds'
import type { OfferSummariesResponse, OfferSummary, UserInfo } from '@/types/api'

/**
 * The dashboard's landing data: one cap summary per offer the viewer can see.
 *
 * `path` differs by surface — `/api/dashboard/offers` for a logged-in affiliate,
 * `/api/view/<token>/offers` for the no-login share link the partners portal embeds —
 * but the payload and the math behind it are identical, so both surfaces render the
 * same list component.
 */
export function useOfferSummaries(path: string) {
  const [offers, setOffers] = useState<OfferSummary[]>([])
  const [user, setUser] = useState<UserInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const refresh = useCallback(async () => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    // A failed poll clears its error on the next SUCCESS rather than blanking it up front, so a
    // transient failure never flashes the list back to an empty state mid-refresh.
    try {
      const data = await fetchAPI<OfferSummariesResponse>(path, {}, controller.signal)
      setOffers(data.offers ?? [])
      setUser(data.user ?? null)
      setError(null)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      setError(err instanceof Error ? err.message : 'Failed to load offers')
    } finally {
      setIsLoading(false)
    }
  }, [path])

  // `isLoading` starts true and is cleared by the first `refresh()`; the poll that follows
  // refreshes in place rather than flashing the list back to a spinner every five minutes.
  // (eslint's react-hooks/set-state-in-effect flags this fetch-on-mount shape; it is the same
  // one DashboardContext and every other data hook here uses, and lint is advisory in this repo.)
  useEffect(() => {
    refresh()
    const id = setInterval(refresh, POLL_INTERVAL_MS)
    return () => {
      clearInterval(id)
      abortRef.current?.abort()
    }
  }, [refresh])

  return { offers, user, isLoading, error, refresh, setUser }
}
